import { TOAST_TYPE } from '@/constans/toast';
import { ReactNode } from 'react';
import { Toaster as Sonner, toast, type ToasterProps } from 'sonner';

const SvgToast = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
    >
        <path
            d="M12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12C0 5.37258 5.37258 0 12 0ZM16.2425 7.65763L10.3027 13.5973L7.75717 11.0518C7.28855 10.5832 6.52876 10.5832 6.06012 11.0518C5.5915 11.5204 5.5915 12.2802 6.06012 12.7488L9.36938 16.058C9.88488 16.5736 10.7207 16.5736 11.2362 16.058L17.9395 9.35468C18.4081 8.88606 18.4081 8.12626 17.9395 7.65763C17.4709 7.189 16.7111 7.189 16.2425 7.65763Z"
            fill="#38F24A"
        />
    </svg>
);

function SvgToastError() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
        >
            <path
                d="M10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0ZM9.99 8H9C8.74512 8.00028 8.49997 8.09788 8.31463 8.27285C8.1293 8.44782 8.01777 8.68695 8.00283 8.94139C7.98789 9.19584 8.07067 9.44638 8.23426 9.64183C8.39785 9.83729 8.6299 9.9629 8.883 9.993L9 10V14.99C9 15.51 9.394 15.94 9.9 15.994L10.01 16H10.5C10.7103 16 10.9153 15.9337 11.0857 15.8105C11.2562 15.6873 11.3835 15.5135 11.4495 15.3139C11.5155 15.1142 11.5169 14.8988 11.4534 14.6982C11.3899 14.4977 11.2649 14.3223 11.096 14.197L11 14.134V9.01C11 8.49 10.606 8.06 10.1 8.006L9.99 8ZM10 5C9.73478 5 9.48043 5.10536 9.29289 5.29289C9.10536 5.48043 9 5.73478 9 6C9 6.26522 9.10536 6.51957 9.29289 6.70711C9.48043 6.89464 9.73478 7 10 7C10.2652 7 10.5196 6.89464 10.7071 6.70711C10.8946 6.51957 11 6.26522 11 6C11 5.73478 10.8946 5.48043 10.7071 5.29289C10.5196 5.10536 10.2652 5 10 5Z"
                fill="#D64C45"
            />
        </svg>
    );
}

import React from 'react';

const Toaster = ({ ...props }: ToasterProps) => {
    return (
        <Sonner
            theme="dark"
            className="toaster group"
            icons={{
                success: (
                    <div>
                        <SvgToast />
                    </div>
                ),
                error: (
                    <div>
                        <SvgToastError />
                    </div>
                ),
            }}
            style={
                {
                    '--normal-bg': 'var(--popover)',
                    '--normal-text': 'var(--popover-foreground)',
                    '--normal-border': 'var(--border)',
                } as React.CSSProperties
            }
            {...props}
        />
    );
};

export function ToastIcon({ type }: { type: 'error' | 'success' | 'loading' }) {
    if (type == 'error') {
        return <SvgToastError />;
    }

    if (type === 'success') {
        return <SvgToast />;
    }

    if (type === 'loading') {
        return (
            <div
                className="animate-spin inline-block size-6 border-3 border-[#333333] border-t-white text-white rounded-full dark:text-blue-500"
                role="status"
                aria-label="loading"
            />
        );
    }

    return null;
}

function ToastCusom({
    id,
    type,
    children,
}: {
    id: string;
    type: 'error' | 'success' | 'loading';
    children?: ReactNode;
}) {
    const color = TOAST_TYPE[type];

    return (
        <div className="flex items-center justify-between gap-3 bg-[#1C1C1C] p-4 pr-5 border border-[#292929] rounded-full w-[80%] mx-auto">
            <ToastIcon type={type} />

            <div
                className="flex-1 text-base font-bold text-white"
                style={{ color: color }}
            >
                {children}
            </div>

            <button
                onClick={() => toast.dismiss(id)}
                className="flex-shrink-0 w-5 h-5"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.99847 8.17781L11.7126 12.892C11.8698 13.0438 12.0803 13.1278 12.2988 13.1259C12.5173 13.124 12.7263 13.0363 12.8808 12.8818C13.0353 12.7273 13.123 12.5183 13.1249 12.2998C13.1268 12.0813 13.0428 11.8708 12.891 11.7136L8.1768 6.99947L12.891 2.28531C13.0428 2.12814 13.1268 1.91764 13.1249 1.69914C13.123 1.48064 13.0353 1.27163 12.8808 1.11712C12.7263 0.962618 12.5173 0.874977 12.2988 0.873078C12.0803 0.87118 11.8698 0.955175 11.7126 1.10697L6.99847 5.82114L2.2843 1.10697C2.12643 0.958927 1.91715 0.878111 1.70074 0.881625C1.48434 0.885139 1.2778 0.972708 1.12481 1.1258C0.971825 1.2789 0.884402 1.4855 0.881041 1.70191C0.877681 1.91831 0.958645 2.12754 1.1068 2.28531L5.82014 6.99947L1.10597 11.7136C1.02638 11.7905 0.962892 11.8825 0.919218 11.9841C0.875544 12.0858 0.852555 12.1952 0.851594 12.3058C0.850632 12.4165 0.871717 12.5262 0.913618 12.6286C0.955519 12.731 1.0174 12.8241 1.09564 12.9023C1.17388 12.9805 1.26693 13.0424 1.36934 13.0843C1.47175 13.1262 1.58149 13.1473 1.69214 13.1463C1.80279 13.1454 1.91214 13.1224 2.01381 13.0787C2.11548 13.035 2.20743 12.9716 2.2843 12.892L6.99847 8.17781Z"
                        fill="white"
                    />
                </svg>
            </button>
        </div>
    );
}

export const toastApp = {
    success: (title: ReactNode, id?: string) => {
        return toast.custom(
            (t: any) => (
                <ToastCusom type="success" id={t.id} children={title} />
            ),
            {
                duration: 3000,
                id,
            }
        );
    },

    error: (title: ReactNode, id?: string) => {
        return toast.custom(
            (t: any) => <ToastCusom type="error" id={t.id} children={title} />,
            {
                duration: 3000,
                id,
            }
        );
    },

    loading: (title: ReactNode, id?: string, duration?: number) => {
        return toast.custom(
            (t: any) => (
                <ToastCusom type="loading" id={t.id} children={title} />
            ),
            {
                duration: duration ?? Infinity,
                id,
            }
        );
    },
};

export { Toaster };
